import { db, auth } from '../firebase-config';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';

// Check if user already exists in database
export const checkUserExists = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking user existence:', error);
    return false;
  }
};

// Create new user in database
export const createUserInDatabase = async (userData) => {
  try {
    const userDoc = {
      uid: userData.uid,
      email: userData.email.toLowerCase(),
      displayName: userData.displayName,
      isVerified: false,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      verificationSentAt: serverTimestamp()
    };

    await setDoc(doc(db, 'users', userData.uid), userDoc);
    console.log('✅ User created in database:', userData.email);
    return { success: true };
  } catch (error) {
    console.error('❌ Error creating user in database:', error);
    return { success: false, error: error.message };
  }
};

// Complete user registration process
export const registerUser = async (email, password, displayName) => {
  try {
    console.log('🔐 Starting registration for:', email);
    
    // Check if user already exists in database
    const userExists = await checkUserExists(email);
    if (userExists) {
      return { 
        success: false, 
        error: 'An account with this email already exists. Please sign in instead.' 
      };
    }

    // Create Firebase auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✅ Firebase user created, updating profile...');
    
    // Update profile with display name
    await updateProfile(user, {
      displayName: displayName
    });

    // Create user in Firestore database
    const dbResult = await createUserInDatabase({
      uid: user.uid,
      email: user.email,
      displayName: displayName
    });

    if (!dbResult.success) {
      console.error('❌ Database creation failed, but keeping auth user for recovery');
      // Don't delete auth user - allow manual recovery
      return { 
        success: false, 
        error: 'Account created but database sync failed. Please contact support.' 
      };
    }

    console.log('📧 Sending verification email...');
    
    // Send email verification
    await sendEmailVerification(user);
    
    console.log('✅ Registration complete for:', email);
    
    // Sign out user until they verify email
    await signOut(auth);
    
    return { 
      success: true, 
      message: 'Account created successfully! Please check your email for verification link.',
      email: email
    };
  } catch (error) {
    console.error('❌ Registration error:', error);
    
    let errorMessage = 'Failed to create account. Please try again.';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered. Please sign in instead.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters long.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your internet connection.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Sign in user with verification check
export const signInUser = async (email, password) => {
  try {
    console.log('🔐 Attempting sign in for:', email);
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get user data from database
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    // SYNC FIREBASE VERIFICATION STATUS WITH FIRESTORE
    if (user.emailVerified) {
      await updateDoc(doc(db, 'users', user.uid), {
        isVerified: true,
        verifiedAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      });
      console.log('✅ Synced verification status for:', email);
    }
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist (backward compatibility)
      await createUserInDatabase({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });
    }
    
    const userData = userDoc.exists() ? userDoc.data() : {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      isVerified: user.emailVerified
    };
    
    // Check if email is verified in Firebase AND in our database
    if (!user.emailVerified || !userData.isVerified) {
      console.log('⚠️ User not verified, sending new verification email...');
      
      // Update verification sent time
      await updateDoc(doc(db, 'users', user.uid), {
        verificationSentAt: serverTimestamp()
      });
      
      // Send verification email again
      await sendEmailVerification(user);
      await signOut(auth);
      
      return { 
        success: false, 
        error: 'Please verify your email address before signing in. We have sent a new verification link.',
        needsVerification: true
      };
    }
    
    // Update last login time
    await updateDoc(doc(db, 'users', user.uid), {
      lastLogin: serverTimestamp(),
      isVerified: true // Ensure sync with Firebase
    });
    
    console.log('✅ Sign in successful for:', email);
    
    return { 
      success: true, 
      user: { ...userData, isVerified: true },
      message: 'Successfully signed in! Welcome back.'
    };
  } catch (error) {
    console.error('❌ Sign in error:', error);
    
    let errorMessage = 'Failed to sign in. Please check your email and password.';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email. Please sign up first.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Verify user email (called when user clicks verification link)
export const verifyUserEmail = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isVerified: true,
      verifiedAt: serverTimestamp()
    });
    
    console.log('✅ User email verified:', userId);
    return { success: true };
  } catch (error) {
    console.error('❌ Error verifying user email:', error);
    return { success: false, error: error.message };
  }
};

// Resend verification email
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      await sendEmailVerification(user);
      
      // Update verification sent time
      await updateDoc(doc(db, 'users', user.uid), {
        verificationSentAt: serverTimestamp()
      });
      
      return { 
        success: true,
        message: 'Verification email sent! Please check your inbox.'
      };
    }
    return { 
      success: false, 
      error: 'No user is currently signed in.' 
    };
  } catch (error) {
    console.error('Send verification error:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Forgot password
export const forgotPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { 
      success: true,
      message: 'Password reset email sent! Check your inbox.'
    };
  } catch (error) {
    console.error('Password reset error:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Check if user is verified
export const isUserVerified = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.isVerified === true;
    }
    return false;
  } catch (error) {
    console.error('Error checking verification:', error);
    return false;
  }
};